<script setup lang="ts">
import {GroupBy, Upload} from "@/types";
import {useChecksum} from "@/composables/checksum";
import {useUserAvatar} from "@/composables/avatar";
import {useDate} from "@/composables/date";
import MetadataDialog from "@/Pages/projects/components/MetadataDialog.vue";
import InertiaLink from "@/Pages/projects/components/InertiaLink.vue";
import {route} from "ziggy-js";
import CopyChecksumBtn from "@/components/buttons/CopyChecksumBtn.vue";
import {useDisplay} from "vuetify";

interface Props {
    uploads: GroupBy<Upload>,
}

const props = defineProps<Props>()
const { shortChecksum } = useChecksum()
const { shortDate, fromTimestamp } = useDate()
const { smAndDown, mdAndUp } = useDisplay()
</script>

<template>
    <v-timeline
        align="start"
        side="end"
        class="mx-5"
    >
        <v-timeline-item
            v-for="(group, date) in uploads"
            :key="date"
            dot-color="white"
            icon-color="grey-darken-1"
            :fill-dot="true"
            width="100%"
        >
            <template #icon>
                <v-icon
                    color="grey-lighten-1"
                    size="xs"
                >
                    mdi-calendar
                </v-icon>
            </template>
            <div class="mt-2 text-grey-darken-1 text-body-2">
                Transfers on {{ shortDate(date as string) }}
            </div>
            <v-list
                class="border pb-0 mt-3"
                :rounded="true"
                :slim="true"
                density="compact"
            >
                <v-list-item
                    v-for="upload in group"
                    :key="upload.checksum"
                    lines="two"
                    class="pb-5 pt-3 border-b"
                    density="compact"
                    :slim="true"
                >
                    <v-list-item-title class="text-body-2 font-weight-bold">
                        <inertia-link :href="route('projects.show', {project: upload.project_slug})">
                            {{ upload.project }}
                        </inertia-link>
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-body" />
                    <v-list-item-subtitle
                        v-if="mdAndUp"
                        class="text-body-2 mt-1"
                    >
                        <v-avatar
                            size="32"
                            class="border-sm"
                            rounded="xl"
                        >
                            <v-img
                                :src="useUserAvatar(upload.user)"
                                alt="avatar"
                            />
                        </v-avatar>
                        <inertia-link
                            :href="route('users.show', {user: upload.user})"
                            class="ml-1 font-weight-bold"
                        >
                            {{ upload.user }}
                        </inertia-link> transferred data at {{ fromTimestamp(upload.timestamp) }}
                    </v-list-item-subtitle>
                    <v-list-item-subtitle
                        v-if="smAndDown"
                        class="text-body-2 mt-1"
                    >
                        {{ fromTimestamp(upload.timestamp) }}
                    </v-list-item-subtitle>
                    <template #append>
                        <v-btn-group
                            v-if="mdAndUp"
                            class="mt-2 mr-3"
                        >
                            <copy-checksum-btn :text="upload.checksum" />
                            <default-btn
                                variant="plain"
                                :rounded="true"
                                class="font-weight-medium border bg-transparent text-primary no-cursor"
                                style="min-height: 40px;"
                                :ripple="false"
                                @click.stop
                            >
                                {{ shortChecksum(upload.checksum) }}
                            </default-btn>
                        </v-btn-group>
                        <metadata-dialog :upload="upload" />
                    </template>
                </v-list-item>
            </v-list>
        </v-timeline-item>
    </v-timeline>
</template>

<style scoped>
:deep(.v-timeline-item__opposite) {
  display: none;
}

:deep(.v-timeline-divider) {
  width: 10px;
}

:deep(.v-timeline-item__body) {
  margin-left: 0;
  width: 100%;
}

.v-timeline--vertical.v-timeline--justify-auto {
    grid-template-columns: min-content min-content auto !important;
}

.no-cursor {
    cursor: default !important;
}
</style>
